export const fullPageTypeList = [
  'campaignpage-variant1',
  'campaignpage',
  'promopage',
] as const;
export type FullPageType = typeof fullPageTypeList[number];

export const documentTypeList = [
  'pagecontent',
  'terms',
  'campaignbanner',
  'campaignbannermultiple',
  'featuredpartners',
  'feature_partner_v2',
  'alerts',
  ...fullPageTypeList,
] as const;
export type DocumentType = typeof documentTypeList[number];

export interface PageContent {
  type: 'pagecontent';
  title: string;
  name: string; // UID
  pageSections: PageSection[];
}

export interface PageSection {
  title: string | null;
  content: string; // Rich text
  boxed: boolean;
}

export interface TermsContent {
  type: 'terms';
  name: string; // UID
  content: string;
}

export interface FullPage {
  type: FullPageType;
  name: string; // UID
  mainHeading: string;
  subHeading: string;
  promoPeriodHeading: string;
  promoPeriod: string;
  promoPeriodImageUrlDesktop: string;
  promoPeriodImageUrlMobile: string;
  promoPeriodIcon: string;
  heroImageUrlDesktop: string;
  heroImageUrlMobile: string;
  heroImageDescription: string;
  mainDescriptionHeading: string;
  mainDescription: string;
  actionButton: string;
  showPage: boolean;
  campaignPageBannersHeading: string;
  campaignPageBanners?: CampaignPageBanner[] | null;
  contentBlocks: CampaignPageContentBlock[] | null;
}

export interface CampaignPageBanner {
  campaignPageBannerActionLink: string;
  campaignPageBannerDescription: string;
  campaignPageBannerUrlDesktop: string;
  campaignPageBannerUrlMobile: string;
  campaignPageBannerTitle: string;
}

export interface CampaignPageContentBlock {
  campaignPageBlockTitle: string;
  campaignPageBlockBody: string;
  campaignPageBlockImageUrlDesktop: string;
  campaignPageBlockImageUrlMobile: string;
}
export interface CampaignBanner {
  type: 'campaignbanner';
  name: string; // UID
  mainContent: string;
  subContent: string;
  link: string;
  desktopImageUrl: string;
  mobileImageUrl: string;
  title: string;
  showBanner: boolean;
  previewBanner: boolean;
  skuList: Set<string>;
  position?: number;
}

export interface MultipleCampaignBanner {
  type: 'campaignbannermultiple';
  items: Omit<CampaignBanner, 'name' | 'position' | 'type' | 'skuList'>[];
}

export interface Alert {
  alertIcon: string;
  alertDescription: string;
  showAlert: boolean;
  previewAlert: boolean;
}

export interface AlertContent {
  type: 'alerts';
  items: Alert[];
}

export enum FeaturedPartnerType {
  loyaltyProgram = 'loyalty program',
  giftCard = 'gift card',
  merchandise = 'merchandise',
  retail = 'retail',
}

export interface FeaturedPartner {
  image: string;
  title: string;
  description: string;
  partnerQueryString: string;
  ariaLabel: string;
  previewFeaturedPartner: boolean;
  showFeaturedPartner: boolean;
  ctaText: string | null;
  ctaUrl: string | null;
  type: FeaturedPartnerType;
}

export interface FeaturedPartnerV2 extends Omit<FeaturedPartner, 'image'> {
  desktopImage: string;
  mobileImage: string;
}

export interface MultipleFeaturedPartners {
  type: 'featuredpartners';
  items: FeaturedPartner[];
}

export interface MultipleFeaturedPartnersV2 {
  heading?: string;
  items: FeaturedPartnerV2[];
  type: 'feature_partner_v2';
}

export type CmsPageContentType =
  | CampaignBanner
  | FullPage
  | MultipleCampaignBanner
  | MultipleFeaturedPartners
  | MultipleFeaturedPartnersV2
  | PageContent
  | TermsContent
  | AlertContent;
